import React, { useMemo, useState } from 'react';
import { Button, Col, Row, Spin, Table, Select, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useGetCurrencyRatesQuery } from '../../../GraphQL/hooks/useGetCurrencyRatesQuery';
import { useUpdateCurrencyRateMutation } from '../../../GraphQL/hooks/useUpdateCurrencyRateMutation';
import { CurrencyRate } from '../../../shared/types';
import { convertDateToFormat } from '../../../shared/Utils';

const { Option } = Select;

const CurrencyRatesTable = () => {
    const { data: currencies, loading, refetch } = useGetCurrencyRatesQuery();
    const { mutation, loading: creating } = useUpdateCurrencyRateMutation();

    const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([]);
    const [availableCurrencies] = useState<string[]>([
        'TND',
        'EUR',
        'USD',
        'GBP',
        'CAD',
        'CHF',
        'SAR',
        'DZD',
        'LYD',
        'MAD',
        'EGP',
        'RUB',
        'CNY',
    ]);

    const [refreshing, setRefreshing] = useState(false);

    const fetchAndUpdateRates = async () => {
        if (!selectedCurrencies.length) {
            message.warning('Please select at least one currency');
            return;
        }

        try {
            setRefreshing(true);

            const currenciesParam = selectedCurrencies.join(',');

            const res = await fetch(
                `https://api.currencyapi.com/v3/latest?apikey=${process.env.REACT_APP_API_CURRENCY_KEY}&currencies=${currenciesParam}&base_currency=TND`
            );

            const data = await res.json();

            if (!data?.data) {
                throw new Error('Invalid API response');
            }

            const updatedRates: CurrencyRate[] = selectedCurrencies.map((cur) => ({
                currency: cur,
                rate: data.data[cur]?.value || 0,
            }));

            // 🔥 Save to DB
            await mutation(updatedRates);

            // 🔥 Reload table from DB
            await refetch();

            message.success('Currency rates updated successfully');
        } catch (err) {
            console.error(err);
            message.error('Failed to update currency rates');
        } finally {
            setRefreshing(false);
        }
    };

    const columns = [
        {
            title: 'Logical ID',
            dataIndex: 'logicalId',
            key: 'logicalId',
            width: 100,
        },
        {
            title: 'Currency',
            dataIndex: 'currency',
            key: 'currency',
        },
        {
            title: 'Rate (Base TND)',
            dataIndex: 'rate',
            key: 'rate',
        },
        {
            title: 'Updated date',
            dataIndex: 'updatedDate',
            key: 'updatedDate',
            render: (text, record) => {
                return convertDateToFormat(record.updatedDate, 'DD-MM-YYYY, HH:mm');
            },
        },
    ];

    return (
        <>
            <Row gutter={16} style={{ marginBottom: 20 }}>
                <Col flex="auto">
                    <Select
                        mode="multiple"
                        style={{ width: '100%' }}
                        placeholder="Select currencies to refresh"
                        value={selectedCurrencies}
                        onChange={setSelectedCurrencies}
                    >
                        {availableCurrencies.map((cur) => (
                            <Option key={cur} value={cur}>
                                {cur}
                            </Option>
                        ))}
                    </Select>
                </Col>

                <Col>
                    <Button type="primary" icon={<ReloadOutlined />} onClick={fetchAndUpdateRates} loading={refreshing}>
                        Refresh
                    </Button>
                </Col>
            </Row>

            <Spin spinning={loading || refreshing || creating}>
                <Table dataSource={currencies} columns={columns} pagination={false} rowKey="_id" />
            </Spin>
        </>
    );
};

export default CurrencyRatesTable;
